import { twJoin } from 'tailwind-merge';

import { Button, ButtonText } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { useAnonymousInquiryGroupingEnabled } from '@/hooks/inbox/useGroupedInquiriesEnabled';
import type { InquiryForward, InquiryForwardTarget } from '@/services/conversations/inquiryForward';
import { TOUCH_TARGET_MIN_H } from '@/util/accessibleTouchTarget';
import { getInquirySubject } from '@/util/getInquirySubject';
import { getProfileNameAndInitials } from '@/util/getProfileNameAndInitials';

/** How much of the question an accessible name carries, enough to tell two cards apart. */
const QUESTION_LABEL_LENGTH = 60;

type InquiryForwardCardProps = {
  forward: InquiryForward;
  onOpen: (target: InquiryForwardTarget) => void;
};

/**
 * Names the inquiry a forward note is about and opens the one it became.
 *
 * The note's body carries no link and no inquiry name by design, so this card
 * is the only thing that tells a member with several open questions which one
 * was forwarded. It names it by the inbox subject and the question it was
 * opened with, never by id. The subject alone is not enough, since a member's
 * inquiries are often all filed under one cancer type with no age or
 * diagnosis, and every card would read the same.
 *
 * Everything it shows rides on the message: the API snapshots the title, the
 * opening question and the intended expert onto the forward when it is
 * written, so the card makes no request of its own and a note arriving over
 * realtime renders complete. Do not add a fetch to fill a missing label. A
 * note written before the labels existed carries only the ids and gets the
 * minimal card, the eyebrow and both actions, which still open the right
 * inquiries.
 *
 * The expert is the one the new inquiry is meant for, never its recipient,
 * which is the Ask Anonymously routing account. It is shown behind the same
 * flag as the inbox row, so the card never names an expert the inbox does not.
 *
 * The two actions are stacked full-height boxes rather than a button and a
 * slop-extended link. The card is too narrow to set them side by side, and
 * stacked, the link's slop reached into the button above it, so a tap on the
 * lower edge of "Open inquiry" opened the original instead.
 */
export function InquiryForwardCard({ forward, onOpen }: InquiryForwardCardProps) {
  const { previousInquiry, newInquiry } = forward;

  const isTitleExpertDisplayEnabled = useAnonymousInquiryGroupingEnabled();

  const subject = getInquirySubject(previousInquiry.title);

  const question = previousInquiry.question ?? '';

  const newExpertName =
    isTitleExpertDisplayEnabled && newInquiry.expert
      ? getProfileNameAndInitials(newInquiry.expert).name
      : '';

  const subjectLabel =
    [subject, question.slice(0, QUESTION_LABEL_LENGTH)].filter(Boolean).join(', ') ||
    'the forwarded inquiry';

  const hasLabels = Boolean(subject || question || newExpertName);

  return (
    <VStack className="mt-3 gap-2 rounded-xl border border-outline-200 bg-background-50 p-3">
      <Text className="font-semibold text-xs text-typography-700">Forwarded inquiry</Text>

      {hasLabels ? (
        <VStack className="gap-0.5">
          {subject ? (
            <Text className="font-semibold text-sm text-typography-900">{subject}</Text>
          ) : null}
          {question ? (
            <Text className="text-sm text-typography-700" numberOfLines={2}>
              {question}
            </Text>
          ) : null}
          {newExpertName ? (
            <Text className="text-sm text-typography-700">Now with {newExpertName}</Text>
          ) : null}
        </VStack>
      ) : null}

      <VStack className="items-start gap-3">
        <Button
          size="sm"
          className={TOUCH_TARGET_MIN_H}
          onPress={() => onOpen('new')}
          accessibilityLabel={`Open inquiry: ${subjectLabel}`}>
          <ButtonText>Open inquiry</ButtonText>
        </Button>

        <Button
          variant="link"
          size="sm"
          className={twJoin('h-auto justify-center p-0', TOUCH_TARGET_MIN_H)}
          onPress={() => onOpen('previous')}
          accessibilityLabel={`View original: ${subjectLabel}`}>
          <ButtonText className="text-primary-700 underline">View original</ButtonText>
        </Button>
      </VStack>
    </VStack>
  );
}
